import React from 'react';

/**
 * Creates a keyboard event handler for integer number inputs
 * Prevents invalid characters (e, E, +, etc.) and allows only digits
 * @returns Handler function for onKeyDown event
 */
export const createIntegerKeyDownHandler = (): ((
  event: React.KeyboardEvent<HTMLInputElement>
) => void) => {
  return (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter
    // Allow: arrow keys, home, end
    if (
      [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ].includes(event.key)
    ) {
      return;
    }

    // Allow Ctrl/Cmd + A, C, V, X, Z
    if (
      (event.ctrlKey || event.metaKey) &&
      ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase())
    ) {
      return;
    }

    // Block: e, E, +, - (except at the start for negative numbers)
    if (['e', 'E', '+'].includes(event.key)) {
      event.preventDefault();
      return;
    }

    // Block minus sign if not at the start
    if (event.key === '-' && (event.target as HTMLInputElement).selectionStart !== 0) {
      event.preventDefault();
      return;
    }

    // Only allow digits
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  };
};

/**
 * Creates a keyboard event handler for decimal number inputs
 * Prevents invalid characters (e, E, +, etc.) and allows digits and decimal point
 * @returns Handler function for onKeyDown event
 */
export const createDecimalKeyDownHandler = (): ((
  event: React.KeyboardEvent<HTMLInputElement>
) => void) => {
  return (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter, decimal point
    // Allow: arrow keys, home, end
    if (
      [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ].includes(event.key)
    ) {
      return;
    }

    // Allow Ctrl/Cmd + A, C, V, X, Z
    if (
      (event.ctrlKey || event.metaKey) &&
      ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase())
    ) {
      return;
    }

    // Block: e, E, +
    if (['e', 'E', '+'].includes(event.key)) {
      event.preventDefault();
      return;
    }

    // Block minus sign if not at the start
    if (event.key === '-' && (event.target as HTMLInputElement).selectionStart !== 0) {
      event.preventDefault();
      return;
    }

    // Allow digits and decimal point (for decimal fields)
    if (!/^[\d.]$/.test(event.key)) {
      event.preventDefault();
    }
  };
};

/**
 * Creates an input event handler that filters invalid characters from pasted content
 * Removes 'e', 'E', '+' characters that might be pasted
 * @param onChangeHandler - The original onChange handler to call with cleaned value
 * @returns Handler function for onInput event
 */
export const createIntegerInputHandler = <T extends string>(
  onChangeHandler: (value: T) => void
): ((event: React.FormEvent<HTMLInputElement>) => void) => {
  return (event: React.FormEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const value = input.value;

    // Remove 'e', 'E', '+' characters
    const cleaned = value.replace(/[eE+]/g, '');

    // If value was changed, update it
    if (cleaned !== value) {
      input.value = cleaned;
      onChangeHandler(cleaned as T);
    }
  };
};

/**
 * Creates an input event handler for field-specific changes
 * Used when the handler needs to call a field-specific change handler
 * @param fieldName - The name of the field being changed
 * @param onChangeHandler - The field-specific change handler
 * @returns Handler function for onInput event
 */
export const createFieldInputHandler = (
  fieldName: string,
  onChangeHandler: (field: string, value: string) => void
): ((event: React.FormEvent<HTMLInputElement>) => void) => {
  return (event: React.FormEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const value = input.value;

    // Remove 'e', 'E', '+' characters
    const cleaned = value.replace(/[eE+]/g, '');

    // If value was changed, update it
    if (cleaned !== value) {
      input.value = cleaned;
      onChangeHandler(fieldName, cleaned);
    }
  };
};
